import LZString from 'lz-string'; // LZ-stringライブラリをインストール後、インポートします。

export const createKeybyTimestamp = () => {
  return new Date().getTime().toString();
};

// now we use localStorage because sessionStorage will be lost in some case

// 古いデータをクリーンアップする関数
const cleanupOldData = () => {
  const currentTimeStamp = new Date().getTime();
  const STORAGE_EXPIRATION_MILI_SECOND = 30 * 60 * 1000; // 30分

  for (const key in localStorage) {
    // タイムスタンプキー（数値のみ）のみをチェック
    if (/^\d+$/.test(key)) {
      const timeDifference = currentTimeStamp - Number(key);
      if (timeDifference > STORAGE_EXPIRATION_MILI_SECOND) {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`Failed to remove old localStorage key: ${key}`, e);
        }
      }
    }
  }
};

export const upsertObject = (key, object) => {
  if (!key || !object) {
    return;
  }

  try {
    const compressedData = LZString.compressToUTF16(JSON.stringify(object));
    localStorage.setItem(key, compressedData);
  } catch (e) {
    if (e.name === 'QuotaExceededError') {
      // クォータ超過の場合、古いデータをクリーンアップして再試行
      console.warn('localStorage quota exceeded, cleaning up old data...');
      cleanupOldData();
      
      try {
        localStorage.setItem(key, compressedData);
      } catch (retryError) {
        // 再試行後も失敗した場合、エラーをログに記録
        console.error('Failed to save to localStorage after cleanup:', retryError);
        throw retryError;
      }
    } else {
      throw e;
    }
  }
};

export const getObject = (key) => {
  const compressedString = localStorage.getItem(key);

  if (!compressedString) {
    return undefined;
  }

  const decompressedString = LZString.decompressFromUTF16(compressedString);

  if (decompressedString === null) {
    console.error(`Failed to decompress data for key: ${key}. Data might be corrupted. Removing it.`);
    localStorage.removeItem(key); // 不正なデータを削除
    return undefined;
  }

  try {
    return JSON.parse(decompressedString);
  } catch (e) {
    console.error(`Failed to parse JSON after decompression for key: ${key}. Data might be corrupted. Removing it.`, e);
    localStorage.removeItem(key); // 不正なデータを削除
    return undefined;
  }
};

export const validateSessionkey = (key) => {
  const {
    selectedOutboundFlight, selectedInboundFlight, searchedFlightsInput,
  } = getFlightState(key);
  const { searchedHotelsInput } = getHotelState(key);
  return !!(selectedOutboundFlight && selectedInboundFlight && searchedFlightsInput && searchedHotelsInput);
};

export const duplicateSessionValue = (key) => {
  const value = getObject(key);
  if (!value) {
    // 値が存在しない場合は、新しいキーを返す
    const newKey = createKeybyTimestamp();
    return newKey;
  }
  
  const newKey = createKeybyTimestamp();
  try {
    upsertObject(newKey, value);
    return newKey;
  } catch (e) {
    // 保存に失敗した場合（クリーンアップ後も失敗）、元のキーを返す
    console.warn('Failed to duplicate session value, using original key:', e);
    return key;
  }
};

export const getFlightState = (key) => {
  const state = getObject(key) || {};
  const flight = state.flight || {};
  return flight;
};

export const getHotelState = (key) => {
  const state = getObject(key) || {};
  const hotel = state.hotel || {};
  return hotel;
};

export const getPlanDetailState = (key) => {
  const state = getObject(key) || {};
  const planDetail = state.planDetail || {};
  return planDetail;
};

export const getSelectedPlanOptionsState = (key) => {
  const state = getObject(key) || {};
  const planOptions = state.selectedPlanOptions || {};
  return planOptions;
};

export const getPlanOptionsState = (key) => {
  const state = getObject(key) || {};
  const planOptions = state.planOptions || {};
  return planOptions;
};

export const saveSearchedFlightsInput = (key, searchedFlightsInput) => {
  const state = getObject(key) || {};
  const flight = state.flight || {};
  flight.searchedFlightsInput = searchedFlightsInput;
  const newState = {
    ...state,
    flight,
  };

  return upsertObject(key, newState);
};

export const saveSearchedHotelsInput = (key, searchedHotelsInput) => {
  const state = getObject(key) || {};
  const hotel = state.hotel || {};
  hotel.searchedHotelsInput = searchedHotelsInput;
  const newState = {
    ...state,
    hotel,
  };

  return upsertObject(key, newState);
};

export const saveSelectedOutboundFlight = (key, selectedOutboundFlight) => {
  const state = getObject(key) || {};
  const flight = state.flight || {};
  flight.selectedOutboundFlight = selectedOutboundFlight;
  const newState = {
    ...state,
    flight,
  };

  return upsertObject(key, newState);
};

export const saveSelectedInboundFlight = (key, selectedInboundFlight) => {
  const state = getObject(key) || {};
  const flight = state.flight || {};
  flight.selectedInboundFlight = selectedInboundFlight;
  const newState = {
    ...state,
    flight,
  };

  return upsertObject(key, newState);
};

export const saveSelectedOutboundDate = (key, selectedOutboundDate) => {
  const state = getObject(key) || {};
  const flight = state.flight || {};
  flight.selectedOutboundDate = selectedOutboundDate;
  const newState = {
    ...state,
    flight,
  };

  return upsertObject(key, newState);
};

export const saveSelectedInboundDate = (key, selectedInboundDate) => {
  const state = getObject(key) || {};
  const flight = state.flight || {};
  flight.selectedInboundDate = selectedInboundDate;
  const newState = {
    ...state,
    flight,
  };

  return upsertObject(key, newState);
};


export const saveSelectedPlans = (key, plans) => {
  const state = getObject(key) || {};
  const planDetail = state.planDetail || {};
  planDetail.plans = plans;
  const newState = {
    ...state,
    planDetail,
  };

  return upsertObject(key, newState);
};

export const saveSelectedPlanOptions = (key, selectedPlanOptions) => {
  const state = getObject(key) || {};
  const land = state.selectedLands || {};
  land.selectedPlanOptions = selectedPlanOptions || [];
  const newState = {
    ...state,
    selectedPlanOptions,
  };

  return upsertObject(key, newState);
};

export const savePlanOptions = (key, planOptions) => {
  const state = getObject(key) || {};
  const planOptionsState = planOptions || state.planOptions || {};
  const newState = {
    ...state,
    planOptions: planOptionsState,
  };

  return upsertObject(key, newState);
};

const sessionIdKey = 'si';

// si は従来どおり sessionId 文字列、または { id, k } の JSON（WESTER復帰後も planDetail を参照するため）
const parseSessionInfo = () => {
  const value = localStorage.getItem(sessionIdKey);
  if (!value) {
    return {};
  }
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && parsed.id) {
      return parsed;
    }
  } catch (e) {
    // 旧形式（sessionId 文字列のみ）
  }
  return { id: value };
};

export const getSessionId = () => {
  return parseSessionInfo().id;
};

// 検索〜内容確認で使う localStorage キー（planDetail.plans 参照用）
export const getSearchSessionKey = () => {
  return parseSessionInfo().k;
};

export const saveSessionId = (sessionToken, searchSessionKey) => {
  if (!sessionToken) {
    return;
  }
  const k = searchSessionKey || getSearchSessionKey();
  if (k) {
    localStorage.setItem(sessionIdKey, JSON.stringify({ id: sessionToken, k }));
  } else {
    localStorage.setItem(sessionIdKey, sessionToken);
  }
};

export const saveSelectedParts = (key, parts) => {
  const state = getObject(key) || {};
  const app = state.app || {};
  app.parts = parts;
  const newState = {
    ...state,
    app,
  };

  return upsertObject(key, newState);
};

export const getSelectedParts = (key) => {
  const state = getObject(key) || {};
  const app = state.app || {};
  return app.parts;

};